/**
 * Local edits that are on screen before the server has agreed to them.
 *
 * Pairs with `useApi`: hand it the `startedAt` of the state you are overlaying
 * and this retires each edit as soon as a response that must contain it comes
 * back. The rule itself lives in `lib/pending.ts`, where it can be tested.
 *
 * Lifecycle of one edit:
 *
 *   begin(key, value)   the row changes on screen, request goes out
 *   settle(key, value?) the request succeeded; hold the edit until the server
 *                       list catches up, optionally swapping in what it returned
 *   rollback(key)       the request failed; the screen goes back to the truth
 *
 * Most callers want `useOptimisticWrites` below rather than this directly.
 */

import { useCallback, useEffect, useState } from "react";
import { retireSettled, type PendingWrite } from "../lib/pending";
import { describeError } from "./useApi";
import { useToast } from "./useToast";

export function usePendingWrites<T>(serverStartedAt: number | null) {
  const [writes, setWrites] = useState<PendingWrite<T>[]>([]);

  useEffect(() => {
    setWrites((list) => retireSettled(list, serverStartedAt));
  }, [serverStartedAt]);

  const begin = useCallback((key: string, value: T) => {
    setWrites((list) => [...list.filter((w) => w.key !== key), { key, value, settledAt: null }]);
  }, []);

  const settle = useCallback((key: string, value?: T) => {
    setWrites((list) =>
      list.map((w) =>
        w.key === key
          ? { ...w, value: value === undefined ? w.value : value, settledAt: Date.now() }
          : w,
      ),
    );
  }, []);

  const rollback = useCallback((key: string) => {
    setWrites((list) => list.filter((w) => w.key !== key));
  }, []);

  return { writes, begin, settle, rollback };
}

/** One optimistic write: what it looks like on screen, and how it is sent. */
export interface OptimisticOp<T, R> {
  /** Identity of the edit. Reusing a key replaces the edit under it. */
  key: string;
  /** How the screen should read while this is in the air. */
  edit: T;
  send: () => Promise<R>;
  /** Ukrainian, ends in a full stop — the toast appends the reason. */
  failure: string;
  /** Runs the same write again from the toast. */
  retry: () => void;
  /** Fold what the server answered back into the on-screen edit. */
  absorb?: (result: R, edit: T) => T;
}

/**
 * The write half of an optimistic screen: apply, send, reconcile, or undo it
 * and say why.
 *
 * Both screens that do this — the task panel and the goals list — had the same
 * `setBusy(true) / await / refetch / setBusy(false)` shape, which is what made
 * a single click freeze a whole page. They now share this instead, so the two
 * cannot drift into behaving differently about the same failure.
 *
 * @param serverStartedAt `ApiState.startedAt` of the data being overlaid
 * @param refetch         the same state's `refetch`; called after a write lands
 */
export function useOptimisticWrites<T>(serverStartedAt: number | null, refetch: () => void) {
  const { writes, begin, settle, rollback } = usePendingWrites<T>(serverStartedAt);
  const toast = useToast();

  const run = useCallback(
    async <R,>(op: OptimisticOp<T, R>): Promise<boolean> => {
      begin(op.key, op.edit);
      try {
        const result = await op.send();
        settle(op.key, op.absorb ? op.absorb(result, op.edit) : undefined);
        // Background: `useApi` keeps the old data and flips `refreshing`, so
        // the list reconciles without blinking.
        refetch();
        return true;
      } catch (err) {
        rollback(op.key);
        toast({
          message: op.failure,
          detail: describeError(err).message,
          // Keyed by the edit, so retrying the same row replaces its own
          // complaint instead of stacking a second one.
          key: `write:${op.key}`,
          action: { label: "Повторити", onSelect: op.retry },
        });
        return false;
      }
    },
    [begin, settle, rollback, refetch, toast],
  );

  return { writes, run };
}
